import { MutableRefObject, useEffect, useState } from "react";
import { BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Bar } from "recharts";
import { getCurrencyCompactFormatter, getCurrencyVerboseFormatter } from "../../util/formatting";
import { useTheme } from "@mui/material";


const currencyCompact = getCurrencyCompactFormatter();
const currencyVerbose = getCurrencyVerboseFormatter();

export type BarChartByBankAccountProps = {
  byBankAccount: Record<string, number>;
  parentRef: MutableRefObject<HTMLElement | undefined>;
};

export function BarChartByBankAccount(props: BarChartByBankAccountProps) {
  const theme = useTheme();
  const { parentRef, byBankAccount } = props;

  const [chartWidth, setChartWidth] = useState(10);

  const chartData = Object.keys(byBankAccount)
    .map(k => ({ accountName: k, amount: byBankAccount[k] }));

  useEffect(() => {
    const getParentWidth = () => parentRef.current?.offsetWidth ?? 500;
    const handleParentWidth = () => setChartWidth(getParentWidth());

    handleParentWidth();// for page load
    window.addEventListener('resize', handleParentWidth);

    return () => {
      window.removeEventListener('resize', handleParentWidth);
    }
  }, [parentRef]);


  return (
    <BarChart width={chartWidth - 30} height={350} data={chartData} margin={{ left: 0 }}>
      <CartesianGrid stroke={theme.palette.primary.contrastText} strokeDasharray="2" strokeWidth={0.3} />
      <XAxis dataKey="accountName" tick={({ x, y, payload }) => {
        const fontSize = payload.value.length > 12 ? '.4em' : '12px';
        const xPosSubtract = payload.value.length < 5 ? 5 : 25;
        return (
          <text key={payload.value} x={x - xPosSubtract} y={y + 15} style={{ fontSize, fill: theme.palette.primary.contrastText }}>
            {payload.value}
          </text>
        );
      }} />

      <YAxis dataKey="amount" tick={({ x, y, payload }) => {
        return (
          <text key={payload.value} x={x - 40} y={y} style={{ fontSize: '11px', fill: theme.palette.primary.contrastText }}>
            {currencyCompact.format((payload.value as number))}
          </text>
        );
      }} />
      <Tooltip content={<CustomTooltip />} />
      <Bar dataKey="amount" barSize={20} fill={theme.palette.primary.main} />
    </BarChart>
  );
}


function CustomTooltip({ active, payload, label }: any) {
  const theme = useTheme();
  if (active && payload && payload.length) {
    return (
      <div style={{backgroundColor: theme.palette.grey[100], borderRadius: '3px', padding: 9, textAlign: 'start' }}>
        <p>{`${label}, ${currencyVerbose.format(payload[0].value)}`}</p>
      </div>
    );
  }

  return null;
};